import { useState } from 'react';
import { ArtExportError, artFileName, renderArtBlob, saveBlob } from '../../render/exportArt';
import { zipFiles } from '../../render/zip';
import { pageIndex, pageOf } from '../../model/operations';
import { hasVideo } from '../../model/timeline';

const numbered = index => `-${String(index + 1).padStart(2, '0')}`;
// The MP4 painter (and mediabunny with it) loads on the first video export only.
const renderVideo = (doc, options) => import('../../render/exportVideo').then(module => module.renderPageVideo(doc, options));

// Download = the saved composition, rendered by the shared painter. `beforeExport` lets
// the host finish a pending save first and hand back the document it saved (null aborts:
// nothing unsaved is ever downloaded); `onExported` lets it keep each file (one per page,
// with its `pageId`). A carousel downloads every page in one .zip (`pages: 'all'`) or the
// page on the stage. Every page is rendered before anything is saved, so a page that
// cannot be rendered stops the whole download. A file that downloaded but could not be
// kept says so instead of claiming success.
// 2.13.0: a page with videos is an MP4 (1×, whatever `format` and `scale` say for the
// others); `progress` is `{ page, count, value }` while one renders, and `audio: 'dropped'`
// says a file came out silent because the browser could not encode its sound.
export default function useExport({ document: doc, preview, title, beforeExport, onExported }) {
  const [state, setState] = useState({ busy: false, error: null, done: null, progress: null, audio: null });
  const run = async ({ format = 'png', scale = 1, pages = 'current', pageId = null } = {}) => {
    setState({ busy: true, error: null, done: null, progress: null, audio: null });
    try {
      let source = preview || doc;
      if (!preview && beforeExport) {
        const saved = await beforeExport();
        if (!saved) { setState({ busy: false, error: 'save', done: null, progress: null, audio: null }); return null; }
        source = saved;
      }
      const ids = pages === 'all' ? source.pages.map(page => page.id) : [pageOf(source, pageId).id];
      const results = [];
      for (const id of ids) {
        const page = pageOf(source, id);
        if (hasVideo(page)) {
          const at = { page: pageIndex(source, id) + 1, count: ids.length };
          setState(current => ({ ...current, progress: { ...at, value: 0 } }));
          results.push({ ...await renderVideo(source, { pageId: id, onProgress: value => setState(current => ({ ...current, progress: { ...at, value } })) }), format: 'mp4', scale: 1 });
        } else results.push({ ...await renderArtBlob(source, { format, scale, pageId: id }), format, scale });
      }
      const size = scale > 1 ? `-${scale}x` : '';
      const sized = result => (result.format === 'mp4' ? '' : size);
      if (results.length > 1) {
        const files = results.map((result, index) => ({ name: artFileName(title, result.extension, `${numbered(index)}${sized(result)}`), blob: result.blob }));
        saveBlob(await zipFiles(files), artFileName(title, 'zip', size));
      } else {
        const page = source.pages.length > 1 ? numbered(pageIndex(source, ids[0])) : '';
        saveBlob(results[0].blob, artFileName(title, results[0].extension, `${page}${sized(results[0])}`));
      }
      const audio = results.some(result => result.audio === 'dropped') ? 'dropped' : null;
      if (onExported) {
        try { for (const result of results) await onExported({ ...result, document: source }); }
        catch { setState({ busy: false, error: 'persist', done: 'downloaded', progress: null, audio }); return results; }
      }
      setState({ busy: false, error: null, done: onExported ? 'stored' : 'downloaded', progress: null, audio });
      return results;
    } catch (failure) {
      setState({ busy: false, error: failure instanceof ArtExportError ? failure.code : 'render', done: null, progress: null, audio: null });
      return null;
    }
  };
  return { ...state, run };
}
